import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Profile } from '../models/profile.model';
import { environment } from 'src/environments/environment';
import { BypassInterceptorContext } from '../interceptors/http.interceptor';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private destroy$ = new Subject<void>();
  currentMode = 'sandbox';

  public profileDataSubject = new BehaviorSubject<Profile | null>(null);
  profileData$ = this.profileDataSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
  ) { }

  /**
   * Generates a tamper-proofing hash signature for storing ro-level in localStorage
   */
  public signLevel(level: number | string, token: string): string {
    const salt = 'komship_ro_lvl_sec_';
    const str = `${salt}${level}:${(token || '').slice(-16)}`;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash |= 0;
    }
    return hash.toString(36);
  }

  /**
   * Securely saves the ro-level to localStorage with a cryptographic signature,
   * while keeping the in-memory state in sync.
   */
  public setRoLevel(level: number | string): void {
    const token = localStorage.getItem('auth-token') || '';
    const numLevel = Number(level) || 0;
    localStorage.setItem('ro-level', JSON.stringify(numLevel));
    localStorage.setItem('ro-level-sig', this.signLevel(numLevel, token));
    if (this.profileDataSubject.value) {
      this.profileDataSubject.next({
        ...this.profileDataSubject.value,
        rajaongkir_level: numLevel,
      });
    }
  }

  /**
   * Reads and verifies stored ro-level from localStorage.
   * If tampered by user in DevTools, clears it and returns 0.
   */
  public getVerifiedStoredLevel(): number {
    const raw = localStorage.getItem('ro-level');
    const sig = localStorage.getItem('ro-level-sig');
    const token = localStorage.getItem('auth-token') || '';
    if (!raw || !sig || !token) return 0;

    try {
      const level = JSON.parse(raw);
      const expectedSig = this.signLevel(level, token);
      if (sig !== expectedSig) {
        // Tampering detected! Clear tampered values
        localStorage.removeItem('ro-level');
        localStorage.removeItem('ro-level-sig');
        return 0;
      }
      return Number(level);
    } catch {
      return 0;
    }
  }

  /**
   * Returns the verified RajaOngkir level.
   * Prioritizes authoritative in-memory server state, falls back to cryptographically verified storage.
   */
  public getRoLevel(): number {
    if (this.profileDataSubject.value?.rajaongkir_level != null) {
      return Number(this.profileDataSubject.value.rajaongkir_level);
    }
    return this.getVerifiedStoredLevel();
  }

  /**
   * Observable stream of verified RajaOngkir Level.
   */
  public getRoLevel$(): Observable<number> {
    return this.profileData$.pipe(
      filter((p): p is Profile => !!p),
      map((p) => Number(p.rajaongkir_level ?? 0))
    );
  }

  getProfile(): void {
    const currentMode = localStorage.getItem('mode') || 'production';
    this.currentMode = currentMode;

    const baseUrl = environment.SWITCH_URL;
    const endPoint = '/user/api/v1/user/profile';
    const apiUrl = baseUrl + endPoint;

    const token = localStorage.getItem('auth-token');

    const headers = new HttpHeaders({
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    });

    const context = new HttpContext().set(BypassInterceptorContext, true);

    this.httpClient.get<{ data: Profile }>(apiUrl, { headers, context }).subscribe({
      next: (res) => {
        localStorage.setItem('email-user', res.data.email || '');
        if (res.data.is_rajaongkir === true && res.data.is_openapi === true) {
          localStorage.setItem('role-user', 'all-role');
        } else if (res.data.is_rajaongkir === true) {
          localStorage.setItem('role-user', 'raja-ongkir');
        } else {
          localStorage.setItem('role-user', 'open-api');
        }

        const level = res.data.rajaongkir_level ?? 0;
        localStorage.setItem('ro-level', JSON.stringify(level));
        localStorage.setItem('ro-level-sig', this.signLevel(level, token || ''));

        this.profileDataSubject.next(res.data);
      },
      error: (error) => {
      },
    });
  }

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
